import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CreateCostCentrePanel } from '@/components/blocks/cost-centres/create-cost-centre-panel';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import { CostCentresTable, DEFAULT_COST_CENTRE_COLUMNS } from '@/components/blocks/cost-centres/cost-centres-table';
import { CostCentresHierarchicalTable } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import { ConfigureStructureModal } from '@/components/blocks/cost-centres/configure-structure-modal';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Combobox } from '@/components/ui/combobox';
import { ChevronDown, Save, X, Plus, Trash2, Settings2, Users, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { fetchClients, fetchEmployees, getDataMode } from '@/lib/data-service';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import type { ProjectTemplate, CreateTemplateData, TemplateResourceEntry } from '@/types/template.types';
import { TEMPLATE_CATEGORIES } from '@/types/template.types';
import { useTemplates } from '@/contexts/TemplateContext';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { useInvoiceTemplates } from '@/contexts/InvoiceTemplateContext';
import { useEmailTemplates } from '@/contexts/EmailTemplateContext';
import { toast } from 'sonner';

// Mock data - same as project form
const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
const typeOptions = [
  { value: 'fixed-price', label: 'Fixed Price' },
  { value: 'time-and-materials', label: 'Time & Materials' },
];

// Role options sourced from Roles & Rates system (MockOrgDefaultsService).
// Templates capture the list of roles only; hours and rates are set on the project.
const ROLE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Project Manager',     value: 'Project Manager'     },
  { label: 'Technical Architect', value: 'Technical Architect' },
  { label: 'Senior Consultant',   value: 'Senior Consultant'   },
  { label: 'Consultant',          value: 'Consultant'          },
  { label: 'Junior Consultant',   value: 'Junior Consultant'   },
  { label: 'Business Analyst',    value: 'Business Analyst'     },
  { label: 'Developer',           value: 'Developer'           },
  { label: 'QA Tester',           value: 'QA Tester'           },
];

// Form validation schema
const templateFormSchema = z.object({
  templateName: z.string().min(1, 'Template name is required').max(80, 'Template name must not exceed 80 characters'),
  templateDescription: z.string().max(200, 'Template description must not exceed 200 characters').optional(),
  category: z.enum(['Consulting', 'Fixed-Fee Delivery', 'Infrastructure', 'Marketing', 'Internal', 'Other']).optional(),
  defaultStatus: z.string().optional(),
  defaultDurationDays: z.number().int().positive().optional(),
  defaultProjectManager: z.string().optional(),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  priority: z.string().optional(),
  type: z.string().optional(),

  clients: z.string().optional(),
  costCentres: z.array(
    z.object({
      code: z.string().min(1, 'Code is required'),
      name: z.string().min(1, 'Name is required'),
      type: z.string().optional(),
      billable: z.boolean(),
      owner: z.string().optional(),
      description: z.string().optional(),
      status: z.enum(['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled']).optional(),
      costsBudget: z.number(),
      revenueBudget: z.number(),
      hoursBudget: z.number(),
      customFields: z.record(z.string(), z.string()).optional(),
      parentCode: z.string().nullable().optional(),
    })
  ).optional(),
  objectives: z.string().max(2000, 'Objectives must not exceed 2000 characters').optional(),
  keyDeliverables: z.string().max(2000, 'Key deliverables must not exceed 2000 characters').optional(),
  resourceBudget: z.array(
    z.object({
      role: z.string().min(1, 'Role is required'),
    })
  ).optional(),
  // Billable — top-level billing flag
  billable: z.boolean(),
  // T&M-specific
  billingFrequency: z.enum(['weekly', 'fortnightly', 'monthly', 'quarterly', 'on-completion', 'milestone']).optional(),
  // Fixed Fee-specific
  billingTriggerType: z.enum(['date', 'percent-complete']).optional(),
  completionCalcMethod: z.enum(['manual', 'tasks', 'hours', 'milestones']).optional(),
  // Shared
  invoiceTemplateId: z.number().nullable().optional(),
  invoiceEmailTemplateId: z.string().nullable().optional(),
  creditNoteEmailTemplateId: z.string().nullable().optional(),
});

type TemplateFormValues = z.infer<typeof templateFormSchema>;

interface TemplateFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  template?: ProjectTemplate;
  onSave: (data: CreateTemplateData) => void;
}

export function TemplateFormSheet({
  open,
  onOpenChange,
  mode,
  template,
  onSave,
}: TemplateFormSheetProps) {
  const { preferences } = useUserPreferences();
  const { selectableStatuses } = useProjectStatuses();
  const { getFieldConfig } = useFieldConfig();
  const { templates } = useTemplates();
  const { templates: invoiceTemplates } = useInvoiceTemplates();
  const { emailTemplates } = useEmailTemplates();
  const showObjectives = getFieldConfig('objectives')?.fieldState !== 'hidden';
  const showKeyDeliverables = getFieldConfig('keyDeliverables')?.fieldState !== 'hidden';
  const showContentSection = showObjectives || showKeyDeliverables;

  // Respect the Project Creation Settings on the template form so template-based
  // projects offer a consistent set of fields with blank projects. A field set to
  // "Hidden" is removed here; template-specific fields (name, description, category,
  // resource budget, billing) have no creation-setting equivalent and are always shown.
  const showDescription = getFieldConfig('description')?.fieldState !== 'hidden';
  const showPriority = getFieldConfig('priority')?.fieldState !== 'hidden';
  const showType = getFieldConfig('type')?.fieldState !== 'hidden';
  const showStatus = getFieldConfig('status')?.fieldState !== 'hidden';
  const showDuration = getFieldConfig('duration')?.fieldState !== 'hidden';
  const showClients = getFieldConfig('clients')?.fieldState !== 'hidden';
  const showCostCentres = getFieldConfig('costCentres')?.fieldState !== 'hidden';
  const showGeneralSection = showDescription || showPriority || showType || showStatus;
  const [isSaving, setIsSaving] = useState(false);
  const [costCentresOpen, setCostCentresOpen] = useState(false);
  const [resourceBudgetOpen, setResourceBudgetOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(true);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

  // Accordion states - all open by default for template creation
  const [generalOpen, setGeneralOpen] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [managementOpen, setManagementOpen] = useState(true);
  const [billingOpen, setBillingOpen] = useState(true);

  // Determine which side to open from based on sidebar position
  const sheetSide = preferences.sidebarPosition === 'right' ? 'left' : 'right';

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      templateName: template?.templateName || '',
      templateDescription: template?.templateDescription || '',
      category: template?.category,
      defaultStatus: template?.defaultStatus || '',
      defaultDurationDays: template?.defaultDurationDays ?? undefined,
      defaultProjectManager: template?.defaultProjectManager || '',
      description: template?.description || '',
      priority: template?.priority || '',
      type: template?.type || '',
      clients: template?.clients || '',
      costCentres: template?.costCentres ?? [],
      resourceBudget: template?.resourceBudget ?? [],
      billable: template?.billable ?? true,
      billingFrequency: template?.billingFrequency ?? undefined,
      billingTriggerType: template?.billingTriggerType ?? undefined,
      completionCalcMethod: template?.completionCalcMethod ?? undefined,
      invoiceTemplateId: template?.invoiceTemplateId ?? null,
      invoiceEmailTemplateId: template?.invoiceEmailTemplateId ?? null,
      creditNoteEmailTemplateId: template?.creditNoteEmailTemplateId ?? null,
      objectives: template?.objectives || '',
      keyDeliverables: template?.keyDeliverables || '',
    },
  });

  // Watch 'type' (the project type field) to drive conditional billing fields — no separate billingType field
  const watchedBillingType = form.watch('type');

  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);
  const [isStructureView, setIsStructureView] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);

  // Client fetch state — mirrors project-form pattern
  const [fetchedClients, setFetchedClients] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isErrorClients, setIsErrorClients] = useState(false);

  // Employee fetch state — feeds the Default Project Manager combobox
  const [fetchedEmployees, setFetchedEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isErrorEmployees, setIsErrorEmployees] = useState(false);

  /**
   * Converts TemplateCostCentre[] → HierarchicalCostCentre[] for the table components.
   * Uses index+1 as a stable temp ID (no real ID exists until backend creates the project).
   * parentCode is resolved to a numeric parentId by matching codes within the array.
   */
  const costCentreRows = useMemo((): HierarchicalCostCentre[] => {
    const list = form.watch('costCentres') ?? [];
    // Assign temp IDs (1-based index)
    const withIds: HierarchicalCostCentre[] = list.map((cc, i) => ({
      id: i + 1,
      code: cc.code,
      name: cc.name,
      type: cc.type,
      billable: cc.billable,
      owner: cc.owner,
      description: cc.description,
      status: cc.status,
      costsBudget: cc.costsBudget,
      revenueBudget: cc.revenueBudget,
      hoursBudget: cc.hoursBudget,
      actualCosts: 0,
      committedCosts: 0,
      actualRevenue: 0,
      actualHours: 0,
      customFields: cc.customFields,
      parentId: undefined,
      children: [],
    }));
    // Resolve parentCode → parentId
    withIds.forEach((row, i) => {
      const parentCode = list[i]?.parentCode;
      if (parentCode) {
        const parent = withIds.find(r => r.code === parentCode);
        if (parent) row.parentId = parent.id;
      }
    });
    return withIds;
  }, [form.watch('costCentres')]);

  // Fetch clients on mount — mirrors project-form pattern
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const clients = await fetchClients('template-form');
        setFetchedClients(clients.map((c: any) => ({ id: c.id, name: c.name, code: c.code })));
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        setIsErrorClients(true);
      } finally {
        setIsLoadingClients(false);
      }
    };
    const loadEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const employees = await fetchEmployees('template-form', { companyAccessOnly: true });
        setFetchedEmployees(employees.map(e => ({ id: String(e.id), name: e.name })));
      } catch (error) {
        console.error('Failed to fetch employees:', error);
        setIsErrorEmployees(true);
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadClients();
    loadEmployees();
  }, []);

  // Reset form when template changes or modal opens/closes
  useEffect(() => {
    if (open) {
      // Always collapse expanding sections when sheet opens so it starts at normal width
      setCostCentresOpen(false);
      setResourceBudgetOpen(false);
      form.reset({
        templateName: template?.templateName || '',
        templateDescription: template?.templateDescription || '',
        category: template?.category,
        defaultStatus: template?.defaultStatus || '',
        defaultDurationDays: template?.defaultDurationDays ?? undefined,
        defaultProjectManager: template?.defaultProjectManager || '',
        description: template?.description || '',
        priority: template?.priority || '',
        type: template?.type || '',
          clients: template?.clients || '',
        costCentres: template?.costCentres ?? [],
        resourceBudget: template?.resourceBudget ?? [],
        billable: template?.billable ?? true,
        billingFrequency: template?.billingFrequency ?? undefined,
        billingTriggerType: template?.billingTriggerType ?? undefined,
        completionCalcMethod: template?.completionCalcMethod ?? undefined,
        invoiceTemplateId: template?.invoiceTemplateId ?? null,
        invoiceEmailTemplateId: template?.invoiceEmailTemplateId ?? null,
        creditNoteEmailTemplateId: template?.creditNoteEmailTemplateId ?? null,
        objectives: template?.objectives || '',
        keyDeliverables: template?.keyDeliverables || '',
      });
    }
  }, [open, template, form]);

  const handleSubmit = async (data: TemplateFormValues) => {
    // Uniqueness check — name must be unique across all templates (excluding current template in edit mode)
    const duplicate = templates.find(
      (t) =>
        t.templateName.trim().toLowerCase() === data.templateName.trim().toLowerCase() &&
        t.id !== template?.id
    );
    if (duplicate) {
      form.setError('templateName', {
        type: 'manual',
        message: `A template named "${duplicate.templateName}" already exists. Please choose a different name.`,
      });
      return;
    }

    try {
      setIsSaving(true);
      await onSave(data);
      toast.success(mode === 'create' ? 'Template created successfully' : 'Template updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(mode === 'create' ? 'Failed to create template' : 'Failed to update template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (form.formState.isDirty) {
      setUnsavedChangesDialogOpen(true);
    } else {
      form.reset();
      onOpenChange(false);
    }
  };

  const handleDiscardChanges = () => {
    setUnsavedChangesDialogOpen(false);
    form.reset();
    onOpenChange(false);
  };

  const handleSheetOpenChange = (newOpen: boolean) => {
    if (!newOpen && form.formState.isDirty) {
      setUnsavedChangesDialogOpen(true);
    } else {
      onOpenChange(newOpen);
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent
        side={sheetSide}
        className={cn(
          'w-full p-0 flex flex-col h-full transition-[max-width] duration-300 ease-in-out',
          costCentresOpen ? 'sm:max-w-[95vw]' : 'sm:max-w-2xl'
        )}
      >
        {/* Fixed Header */}
        <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
          <SheetHeader>
            <SheetTitle>
              {mode === 'create' ? 'Create Template' : 'Edit Template'}
            </SheetTitle>
            <SheetDescription>
              {mode === 'create'
                ? 'Create a reusable template with pre-filled fields for faster project creation.'
                : 'Update your template configuration.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable Content */}
            <ScrollArea className="flex-1 min-h-0">
              <ScrollAreaViewport className="h-full max-h-full">
                <div className="px-6 py-4 space-y-6">
              {/* Template Identity Section */}
              <div className="rounded-lg border bg-card">
                <div className="flex items-center justify-between p-4">
                  <h3 className="text-lg font-semibold">Template Identity</h3>
                </div>
                <div className="p-4 pt-0 space-y-4">

                <FormField
                  control={form.control}
                  name="templateName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Template Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Software Development Project" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="templateDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe what this template is for and when to use it..."
                          className="resize-none"
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ''}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TEMPLATE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </div>
              </div>

              {/* General Information Accordion */}
              {showGeneralSection && (
              <div className="rounded-lg border bg-card">
              <Collapsible open={generalOpen} onOpenChange={setGeneralOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <h3 className="text-lg font-semibold">General Information</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      generalOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-4">
                  {showDescription && (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Default project description..."
                            className="resize-none"
                            rows={4}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    {showPriority && (
                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select priority" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {priorityOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    )}

                    {showType && (
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {typeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    )}

                    {showStatus && (
                    <FormField
                      control={form.control}
                      name="defaultStatus"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Default Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a default status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {selectableStatuses.map((status) => (
                                <SelectItem key={status.id} value={String(status.id)}>
                                  {status.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    )}
                  </div>

                </CollapsibleContent>
              </Collapsible>
              </div>
              )}

              {/* Timeline Accordion */}
              {showDuration && (
              <div className="rounded-lg border bg-card">
              <Collapsible open={timelineOpen} onOpenChange={setTimelineOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <h3 className="text-lg font-semibold">Timeline</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      timelineOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-4">
                  <FormField
                    control={form.control}
                    name="defaultDurationDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Duration (days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            placeholder="e.g. 30"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>
              </div>
              )}

              {/* Management Accordion */}
              <div className="rounded-lg border bg-card">
              <Collapsible open={managementOpen} onOpenChange={setManagementOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <h3 className="text-lg font-semibold">Management</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      managementOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-4">
                  <FormField
                    control={form.control}
                    name="defaultProjectManager"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Project Manager</FormLabel>
                        {isLoadingEmployees ? (
                          <Skeleton className="h-9 w-full" />
                        ) : isErrorEmployees ? (
                          <Alert variant="destructive" size="compact">
                            <AlertDescription>Unable to load employees. Please refresh and try again.</AlertDescription>
                          </Alert>
                        ) : (
                          <FormControl>
                            <Combobox
                              options={fetchedEmployees.map(e => ({ value: e.id, label: e.name }))}
                              value={field.value || ''}
                              onValueChange={field.onChange}
                              placeholder="Select project manager..."
                              searchPlaceholder="Search employees..."
                              emptyMessage="No employees found."
                            />
                          </FormControl>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Pre-fills the Project Manager on projects created from this template. It can still be changed during creation.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {showClients && (
                  <FormField
                    control={form.control}
                    name="clients"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Client</FormLabel>
                        {isLoadingClients ? (
                          <Skeleton className="h-9 w-full" />
                        ) : isErrorClients ? (
                          <Alert variant="destructive" size="compact">
                            <AlertDescription>Unable to load clients. Please refresh and try again.</AlertDescription>
                          </Alert>
                        ) : (
                          <FormControl>
                            <Combobox
                              options={
                                fetchedClients.map(c => ({
                                  value: c.id,
                                  label: c.code ? `${c.code} — ${c.name}` : c.name,
                                }))
                              }
                              value={field.value || ''}
                              onValueChange={field.onChange}
                              placeholder="Select client..."
                              searchPlaceholder="Search clients..."
                              emptyMessage="No clients found."
                            />
                          </FormControl>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  )}
                </CollapsibleContent>
              </Collapsible>
              </div>

              {/* Cost Centres Accordion */}
              {showCostCentres && (
              <div className="rounded-lg border bg-card">
              <Collapsible open={costCentresOpen} onOpenChange={setCostCentresOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Cost Centres</h3>
                    {(form.watch('costCentres')?.length ?? 0) > 0 && (
                      <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {form.watch('costCentres')?.length}
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      costCentresOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Cost centres defined here are copied to every project created from this template —
                    including type, billable flag, owner, budget values, and hierarchy structure.
                    Set them up correctly once here so each project starts ready to track against.
                  </p>

                  {/* Toolbar — mirrors the project details cost centres tab exactly */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isStructureView && (
                      <Button
                        type="button"
                        size="sm"
                        className="gap-2"
                        onClick={() => setCreateCostCentrePanelOpen(true)}
                      >
                        <Plus className="h-4 w-4" />
                        New
                      </Button>
                    )}
                    {isStructureView && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => setConfigureStructureOpen(true)}
                      >
                        <Settings2 className="h-4 w-4" />
                        Configure Structure
                      </Button>
                    )}
                    {/* Spacer */}
                    <div className="flex-1" />
                    {/* Structure View toggle */}
                    <div className="flex items-center gap-2">
                      <label htmlFor="template-structure-view-toggle" className="text-sm font-medium cursor-pointer">
                        Structure View
                      </label>
                      <Switch
                        id="template-structure-view-toggle"
                        checked={isStructureView}
                        onCheckedChange={setIsStructureView}
                      />
                    </div>
                  </div>

                  {/* Tables — same components as project details tab, no card wrapper */}
                  {isStructureView ? (
                    <CostCentresHierarchicalTable
                      data={costCentreRows}
                      columns={DEFAULT_COST_CENTRE_COLUMNS.filter(c =>
                        ['code', 'name', 'type', 'status', 'billable', 'costsBudget', 'revenueBudget', 'hoursBudget'].includes(c.key)
                      ).map(c => ({ ...c, visible: true }))}
                      useCardWrapper={false}
                    />
                  ) : (
                    <CostCentresTable
                      data={costCentreRows}
                      columns={DEFAULT_COST_CENTRE_COLUMNS.filter(c =>
                        ['code', 'name', 'type', 'status', 'billable', 'costsBudget', 'revenueBudget', 'hoursBudget'].includes(c.key)
                      ).map(c => ({ ...c, visible: true }))}
                      useCardWrapper={false}
                      onRowClick={undefined}
                    />
                  )}

                  {/* Empty state when no cost centres added yet */}
                  {(form.watch('costCentres')?.length ?? 0) === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No cost centres added yet. Click <strong>New</strong> to add the first one.
                    </p>
                  )}
                </CollapsibleContent>
              </Collapsible>
              </div>
              )}

              {/* Panels rendered outside the collapsible so Sheets stack correctly */}
              <CreateCostCentrePanel
                open={createCostCentrePanelOpen}
                onOpenChange={setCreateCostCentrePanelOpen}
                existingCodes={(form.watch('costCentres') ?? []).map(cc => cc.code)}
                onSave={(newCc: Omit<CostCentre, 'id'>) => {
                  const current = form.getValues('costCentres') ?? [];
                  form.setValue('costCentres', [
                    ...current,
                    {
                      code: newCc.code,
                      name: newCc.name,
                      type: newCc.type,
                      billable: newCc.billable,
                      owner: newCc.owner,
                      description: newCc.description,
                      status: newCc.status,
                      costsBudget: newCc.costsBudget,
                      revenueBudget: newCc.revenueBudget,
                      hoursBudget: newCc.hoursBudget,
                      customFields: newCc.customFields
                        ? Object.fromEntries(
                            Object.entries(newCc.customFields).map(([k, v]) => [k, String(v)])
                          )
                        : undefined,
                      parentCode: null,
                    },
                  ]);
                }}
              />

              {/* ConfigureStructureModal — lets users define parent/child relationships between cost centres */}
              <ConfigureStructureModal
                open={configureStructureOpen}
                onOpenChange={setConfigureStructureOpen}
                projectName="Template"
                costCentres={costCentreRows}
                onSave={async (updatedStructure) => {
                  // Flatten the updated hierarchy back to TemplateCostCentre[]
                  // preserving parentCode references by code rather than numeric ID
                  const flatten = (nodes: HierarchicalCostCentre[], parentCode: string | null = null): Array<typeof nodes[0] & { resolvedParentCode: string | null }> => {
                    return nodes.flatMap(node => [
                      { ...node, resolvedParentCode: parentCode },
                      ...(node.children ? flatten(node.children, node.code) : []),
                    ]);
                  };

                  const flatNodes = flatten(updatedStructure);
                  const current = form.getValues('costCentres') ?? [];

                  // Merge: preserve existing TemplateCostCentre fields, update parentCode from new structure
                  const updated = flatNodes.map(node => {
                    const existing = current.find(cc => cc.code === node.code);
                    return {
                      code: node.code,
                      name: node.name,
                      type: node.type,
                      billable: node.billable,
                      owner: node.owner,
                      description: node.description,
                      status: node.status,
                      costsBudget: node.costsBudget,
                      revenueBudget: node.revenueBudget,
                      hoursBudget: node.hoursBudget,
                      customFields: existing?.customFields,
                      parentCode: node.resolvedParentCode,
                    };
                  });

                  form.setValue('costCentres', updated);
                }}
              />

              {/* Resource Budget Accordion */}
              <div className="rounded-lg border bg-card">
              <Collapsible open={resourceBudgetOpen} onOpenChange={setResourceBudgetOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Resource Budget</h3>
                    {(form.watch('resourceBudget')?.length ?? 0) > 0 && (
                      <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {form.watch('resourceBudget')?.length}
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      resourceBudgetOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Define the roles budgeted for projects using this template.
                    Hours are entered monthly on the project and rates are derived from Roles &amp; Rates.
                  </p>

                  {/* Table header */}
                  {(form.watch('resourceBudget')?.length ?? 0) > 0 && (
                    <div className="rounded-md border overflow-hidden">
                      {/* Column headers */}
                      <div className="grid grid-cols-[1fr_36px] gap-2 px-3 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground">
                        <span>Role</span>
                        <span />
                      </div>

                      {/* Role rows */}
                      {form.watch('resourceBudget')?.map((entry, index) => {
                        return (
                          <div
                            key={index}
                            className="grid grid-cols-[1fr_36px] gap-2 px-3 py-2 items-center border-b last:border-b-0"
                          >
                            {/* Role combobox */}
                            <Combobox
                              options={ROLE_OPTIONS}
                              value={entry.role}
                              onValueChange={(val) => {
                                const current = form.getValues('resourceBudget') ?? [];
                                const updated = current.map((e, i) =>
                                  i === index ? { ...e, role: val } : e
                                );
                                form.setValue('resourceBudget', updated, { shouldDirty: true });
                              }}
                              placeholder="Select role"
                              searchPlaceholder="Search roles..."
                              emptyMessage="No roles found."
                            />

                            {/* Remove row */}
                            <button
                              type="button"
                              aria-label="Remove role"
                              className="flex items-center justify-center h-7 w-7 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              onClick={() => {
                                const current = form.getValues('resourceBudget') ?? [];
                                form.setValue(
                                  'resourceBudget',
                                  current.filter((_, i) => i !== index),
                                  { shouldDirty: true }
                                );
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Empty state */}
                  {(form.watch('resourceBudget')?.length ?? 0) === 0 && (
                    <div className="flex flex-col items-center justify-center py-6 border border-dashed rounded-md text-center gap-2">
                      <Users className="h-7 w-7 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">
                        No roles added yet. Click <strong>Add Role</strong> to start building the resource budget.
                      </p>
                    </div>
                  )}

                  {/* Add role button */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      const current = form.getValues('resourceBudget') ?? [];
                      form.setValue('resourceBudget', [
                        ...current,
                        { role: '' },
                      ], { shouldDirty: true });
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Role
                  </Button>
                </CollapsibleContent>
              </Collapsible>
              </div>

              {/* Billing Configuration Accordion */}
              <div className="rounded-lg border bg-card">
              <Collapsible open={billingOpen} onOpenChange={setBillingOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                  <h3 className="text-lg font-semibold">Billing Configuration</h3>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-muted-foreground transition-transform duration-200',
                      billingOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Set default billing configuration for projects created from this template. These values pre-fill the billing fields on project creation and can be adjusted per project. Billing options are driven by the <strong>Type</strong> selected in the General section above.
                  </p>

                  {/* Billable toggle — always visible */}
                  <FormField
                    control={form.control}
                    name="billable"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">Billable</FormLabel>
                          <p className="text-xs text-muted-foreground">Projects created from this template will be marked as billable by default</p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value ?? true}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* T&M conditional fields — shown when Type = Time & Materials */}
                  {watchedBillingType === 'time-and-materials' && (
                    <div className="space-y-4 pt-1">
                      <FormField
                        control={form.control}
                        name="billingFrequency"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Billing Frequency</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value ?? ''}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="quarterly">Quarterly</SelectItem>
                                <SelectItem value="on-completion">On Completion</SelectItem>
                                <SelectItem value="milestone">Milestone</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="invoiceEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Invoice Email Template</FormLabel>
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select email template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'invoice')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="creditNoteEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Credit Note Template</FormLabel>
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select credit note template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'credit_note')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="invoiceTemplateId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Invoice Template</FormLabel>
                            <Select
                              onValueChange={(val) => field.onChange(val === '' ? null : Number(val))}
                              value={field.value != null ? String(field.value) : ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select invoice template" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {invoiceTemplates.map((t) => (
                                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Fixed Fee conditional fields */}
                  {watchedBillingType === 'fixed-price' && (
                    <div className="space-y-4 pt-1">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="billingTriggerType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Billing Trigger</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select trigger" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="date">Milestone Date</SelectItem>
                                  <SelectItem value="percent-complete">% Complete</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="completionCalcMethod"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Completion % Calculation</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select method" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="manual">Manual Entry</SelectItem>
                                  <SelectItem value="tasks">Based on Tasks Completed</SelectItem>
                                  <SelectItem value="hours">Based on Hours Logged</SelectItem>
                                  <SelectItem value="milestones">Based on Milestones</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="invoiceEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Invoice Email Template</FormLabel>
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select email template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'invoice')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="creditNoteEmailTemplateId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Credit Note Template</FormLabel>
                              <Select
                                onValueChange={(val) => field.onChange(val === '' ? null : val)}
                                value={field.value ?? ''}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select credit note template" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {emailTemplates
                                    .filter((t) => t.type === 'credit_note')
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="invoiceTemplateId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Invoice Template</FormLabel>
                            <Select
                              onValueChange={(val) => field.onChange(val === '' ? null : Number(val))}
                              value={field.value != null ? String(field.value) : ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select invoice template" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {invoiceTemplates.map((t) => (
                                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Placeholder when no type selected in General */}
                  {!watchedBillingType && (
                    <p className="text-sm text-muted-foreground italic">
                      Set the project <strong>Type</strong> in the General section to Fixed Price or Time &amp; Materials to configure billing details.
                    </p>
                  )}
                </CollapsibleContent>
              </Collapsible>
              </div>

              {/* Content Accordion — hidden if both objectives and keyDeliverables are hidden in field config */}
              {showContentSection && (
                <div className="rounded-lg border bg-card">
                <Collapsible open={contentOpen} onOpenChange={setContentOpen}>
                  <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                    <h3 className="text-lg font-semibold">Content</h3>
                    <ChevronDown
                      className={cn(
                        'h-5 w-5 text-muted-foreground transition-transform duration-200',
                        contentOpen && 'rotate-180'
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-4 pt-0 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Pre-fill the objectives and key deliverables for any project created from this template.
                    </p>

                    {/* Objectives — only shown if not hidden in field config */}
                    {showObjectives && (
                      <FormField
                        control={form.control}
                        name="objectives"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Objectives</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="e.g. Deliver a fully tested product increment by end of Q3, aligned with the strategic roadmap..."
                                className="min-h-[120px] resize-y"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Key Deliverables — only shown if not hidden in field config */}
                    {showKeyDeliverables && (
                      <FormField
                        control={form.control}
                        name="keyDeliverables"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Key Deliverables</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="e.g. Working software build, QA sign-off report, updated documentation, stakeholder demo..."
                                className="min-h-[120px] resize-y"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </CollapsibleContent>
                </Collapsible>
                </div>
              )}

                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>

            {/* Sticky Footer */}
            <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto">
              <div className="flex items-center justify-end gap-3 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : mode === 'create' ? 'Create Template' : 'Save Changes'}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>

    {/* Unsaved Changes Guard */}
    <AlertDialog open={unsavedChangesDialogOpen} onOpenChange={setUnsavedChangesDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning-500" />
            Unsaved Changes
          </AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes to this template. If you close now, your changes will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setUnsavedChangesDialogOpen(false)}>
            Keep Editing
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDiscardChanges}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Discard Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
